import React, { useCallback, useEffect, useState } from 'react'
import { SortDescriptor } from '@progress/kendo-data-query'
import { GridColumn as Column, Grid, GridSortChangeEvent } from '@progress/kendo-react-grid'
import { DealDto, DealDtoFilterKey } from 'api/Clients/Types/DealDto'
import { PageData } from 'api/Pagination/Types'
import { IntroducingBrokerDetailsDto } from 'api/Trading-Accounts/Types/IntroducingBrokerDetailsDto'
import { Pager } from 'components/components/Pager'
import { Spinner } from 'components/Spinner'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { CompositeFilterDescriptor, GridFilterActionState, initialState } from 'store/grid'
import { useApiClient } from 'utils/api/ApiClient'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { buildQuery, buildSortQuery, FilterLogic } from 'utils/QueryHelpers'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import introducingBrokerDealsColumn from './introducingBrokerDealsColumn'
import {
  IntroducingBrokerNavigation,
  IntroducingBrokerTab,
} from './Navigation/IntroducingBrokerNavigation'

interface Props {
  data: IntroducingBrokerDetailsDto
}

const IntroducingBrokerDeals: React.FC<Props> = (props) => {
  const { data } = props
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)

  const [deals, setDeals] = useState<DealDto[]>([])
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (gridFilterState: GridFilterActionState): Promise<PageData<DealDto[]>> => {
          const searchQuery = getQuery(gridFilterState.filter)
          const sortQuery = getSort(gridFilterState.sort)
          const response = await partnershipsApiClient.getDeals({
            search: searchQuery,
            sort: sortQuery,
            pageIndex: gridFilterState.pageIndex,
            pageSize: gridFilterState.pageSize,
            skipCount: tableData?.skipCount,
          })

          if (response?.items) {
            setDeals(response.items)
          }

          return response
        }
      )
    })()
  }, [tableData?.skipCount])

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<DealDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )

    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }
    setTableData({ ...tableData, meta: response })
    gridStatusActions.setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      const gridFilter = filter ?? gridState.filter
      return (
        buildQuery({
          ...gridFilter,
          ...createDealsQuery(data.id).search,
        }) ?? null
      )
    },
    [data.id, gridState.filter]
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(sortDescriptors ?? gridState.sort)
    },
    [gridState.sort]
  )

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    const searchQuery = getQuery(gridState.filter)
    const sortQuery = getSort(event.sort)
    const response = await partnershipsApiClient.getDeals({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      skipCount: tableData?.skipCount,
    })

    if (response?.items) {
      setDeals(response.items)
      setTableData({ ...tableData, meta: response })
    }
  }

  const handlePageSizeChange = async (pageSize: number) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      pageSize,
      take: pageSize,
      skip: 0,
    })

    const response = await partnershipsApiClient.getDeals({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount,
    })

    if (response?.items) {
      setDeals(response.items)
      setTableData({ ...tableData, meta: response })
    }
  }

  const handlePageChange = async (pageIndex: number) => {
    const take = gridState.take || 10
    const skip = (pageIndex - 1) * take

    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    gridActions.setGridFilters({
      take,
      skip,
      pageIndex,
    })

    const response = await partnershipsApiClient.getDeals({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: take,
      skipCount: tableData?.skipCount,
    })

    if (response?.items) {
      setDeals(response.items)
      setTableData({ ...tableData, meta: response })
      gridActions.setGridFilters({
        total: response.totalResults,
      })
    }
  }

  const handleSkipCountChange = async (skipCount: boolean) => {
    setTableData({ ...tableData, skipCount })
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <React.Fragment>
      <IntroducingBrokerNavigation data={data} isEdit={false} tab={IntroducingBrokerTab.DEALS} />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <TableWithExport data={deals} columns={introducingBrokerDealsColumn}>
          <Grid
            {...gridState}
            resizable
            data={deals}
            sort={gridState.sort}
            total={gridState.total}
            take={gridState.take}
            skip={gridState.skip}
            onSortChange={handleSortChange}
            pageable
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChange}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex || 1}
                pageSize={gridState.pageSize || 10}
                take={gridState.take || 10}
                skip={gridState.skip || 0}
              />
            )}
            style={{ zIndex: 0 }}
          >
            {introducingBrokerDealsColumn
              .filter((c) => c.show)
              .map((column, idx) => (
                <Column key={idx + 2} {...column} />
              ))}
          </Grid>
        </TableWithExport>
      </TableStateContext.Provider>
    </React.Fragment>
  )
}

const createDealsQuery = (ibId: string) => {
  const initialGridState = initialState()

  return {
    search: {
      ...initialGridState.filter,
      logic: initialGridState.filter.logic,
      defaultFilters: {
        logic: FilterLogic.AND,
        filters: [
          {
            field: DealDtoFilterKey.introducingBroker,
            operator: '==',
            value: ibId,
          },
        ],
      },
    },
  }
}

export default IntroducingBrokerDeals
